import {
  useEffect, useRef, useMemo, useState,
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import clsx from 'clsx';
import { Button } from '@material-ui/core';
import { useAnalytics } from 'use-analytics';
import ContentItemMenu from './components/contentItemMenu';
import extractBrochureData from './utils/extractBrochureData';
import LinkMenu from './components/LinkPreviewMenu';
import TextMenu from './components/TextMenu';
import { saveBrochureRequest, updateBrochureField, updateBrochureFields } from '../../../store/slices/brochure';
import ExtensionKits from './extensions';
import { SearchStatus, TravelTypes } from '../../../constants';
import { getRoomDescriptionRequest } from '../../../store/slices/hotel';
import { getFullRawDescription } from '../../TravelOption/HotelInformation/HotelDetails/RoomsDetails';
import TableRowMenu from './extensions/Table/menus/TableRow';
import TableColumnMenu from './extensions/Table/menus/TableColumn';

const TipTapEditor = ({
  brochure, isVisible, isEditable, isBrochurePage, richTextButtonStyles, showSpinner,
}) => {
  const menuContainerRef = useRef(null);
  const [didInit, setDidInit] = useState(false);
  const dispatch = useDispatch();
  const {
    doShowChatGPTImprovedRoomDescription,
    cleanseAmenities: doCleanseAmenities,
    brochureSettings: { DisplayName },
  } = useSelector((store) => store.user.travelAgency.settings);
  const displayLabel = DisplayName && DisplayName.trim() ? DisplayName : 'Brochure';
  const { roomDescriptions } = useSelector((state) => state.hotel);
  const roomDescriptionsRef = useRef(roomDescriptions);
  useEffect(() => {
    roomDescriptionsRef.current = roomDescriptions;
  }, [roomDescriptions]);
  const saveButtonClasses = isBrochurePage && richTextButtonStyles();
  const { track } = useAnalytics();
  const stateBrochure = useSelector((store) => store.brochure.brochure);
  const { saveBrochureStatus, saveBrochureError } = stateBrochure;

  useEffect(() => {
    if (!brochure?.trips) return;
    Object.values(brochure.trips).forEach(({ tripSegments }) => {
      tripSegments.forEach(({ itinerary }) => {
        if (itinerary.mode === TravelTypes.hotel) {
          itinerary.hotel.products.forEach((product) => {
            const code = product?.code;
            const currentRoomDesc = roomDescriptionsRef.current[code];

            if (
              code
            && doShowChatGPTImprovedRoomDescription
            && (!currentRoomDesc || !currentRoomDesc.description)
            ) {
              dispatch(getRoomDescriptionRequest({
                code,
                description: getFullRawDescription(product),
                currencyCode: product.roomPrice.propertyCurrencyCode
                || product.roomPrice.currencyCode,
              }));
            }
          });
        }
      });
    });
  }, [brochure.trips, dispatch, doShowChatGPTImprovedRoomDescription]);

  const allProductDescriptionsLoaded = useMemo(() => {
    if (!brochure?.trips) return false;

    return Object.values(brochure.trips).every(
      ({ tripSegments }) => tripSegments.every(({ itinerary }) => {
        if (itinerary.mode !== TravelTypes.hotel) return true;

        return itinerary.hotel.products.every((product) => {
          const desc = roomDescriptions[product.code];
          return desc && desc.description;
        });
      }),
    );
  }, [brochure.trips, roomDescriptions]);

  // Debounced dispatch function for optimization
  const updateEditorHTML = useMemo(() => debounce((content) => {
    dispatch(updateBrochureField({
      brochureId: brochure.id,
      fieldName: 'editorHTML',
      fieldValue: content,
    }));
  }, 500), [brochure.id, dispatch]);
  const updateEditorJSON = useMemo(() => debounce((content) => {
    dispatch(updateBrochureField({
      brochureId: brochure.id,
      fieldName: 'editorJSON',
      fieldValue: content,
    }));
  }, 500), [brochure.id, dispatch]);
  // Cleanup debounce on unmount
  useEffect(() => () => {
    updateEditorHTML.cancel();
    updateEditorJSON.cancel();
  }, [updateEditorHTML, updateEditorJSON]);

  const editor = useEditor({
    onUpdate: (ctx) => {
      updateEditorHTML(ctx.editor.getHTML());
      updateEditorJSON(JSON.stringify(ctx.editor.getJSON()));
      return false;
    },
    editable: isEditable,
    extensions: ExtensionKits(),
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        class: clsx(
          'min-h-[156px] bg-slate-50 py-2  outline-none border-none ring-0 focus:outline-none focus:ring-0 focus:border-none', { 'px-3': isEditable },
        ),
      },
    },
    content: '',
    immediatelyRender: false,
  }, []);
  // Editor Initialization
  useEffect(() => {
    if (!editor || !brochure?.id || didInit) return;
    // Parse and clone the editorJSON to allow safe mutation
    const rawEditorJSON = brochure && brochure.editorJSON;
    const parsedEditorJSON = rawEditorJSON && JSON.parse(typeof rawEditorJSON === 'string' ? rawEditorJSON : JSON.stringify(rawEditorJSON));
    const content = parsedEditorJSON
      || extractBrochureData(brochure, roomDescriptions, doCleanseAmenities);
    const json = editor.schema.nodeFromJSON(content);
    const { state, view } = editor;
    const tr = state.tr.replaceWith(0, state.doc.content.size, json.content);
    tr.setMeta('addToHistory', false); // prevent from being un-doable
    view.dispatch(tr);
    dispatch(updateBrochureField({
      brochureId: brochure.id,
      fieldName: 'editorJSON',
      fieldValue: JSON.stringify(editor.getJSON()),
    }));

    dispatch(updateBrochureField({
      brochureId: brochure.id,
      fieldName: 'editorHTML',
      fieldValue: editor.getHTML(),
    }));
    track('brochureEditor.initialized', {
      brochureId: brochure.id,
      segments: content?.content?.length || 0,
    });
    setDidInit(true);
  }, [editor, brochure, didInit, dispatch, roomDescriptions, track, doCleanseAmenities]);

  if (!editor || (!isBrochurePage && !allProductDescriptionsLoaded)) return null;
  return (
    <div
      className={`transition-opacity duration-200 ease-in-out ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none h-0 overflow-hidden'}`}
      ref={menuContainerRef}
      data-editor-container
    >
      { isEditable
      && isBrochurePage && (
      <p className="text-sm mb-2 p-3 rounded-md border border-blue-200 bg-blue-50 text-blue-800">
        This experimental editor allows you to fully customize the brochure
        content,Try it out during the free trial period!
      </p>
      )}
      <EditorContent
        editor={editor}
        className={clsx('flex-1 overflow-y-auto', { 'pl-12': isEditable })}
      />
      <ContentItemMenu editor={editor} />
      <LinkMenu editor={editor} appendTo={menuContainerRef} />
      <TextMenu editor={editor} appendTo={menuContainerRef} />
      <TableRowMenu editor={editor} appendTo={menuContainerRef} />
      <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
      {isBrochurePage && isEditable && (
      <div className="flex justify-end p-4 mt-4 gap-2 items-center">
        {showSpinner(saveBrochureStatus, saveBrochureError)}
        <Button
          classes={saveButtonClasses}
          onClick={() => {
            dispatch(updateBrochureFields({
              brochureId: brochure.id,
              fields: {
                saveBrochureStatus: SearchStatus.SEARCHING,
                isSaveBrochureContent: true,
              },
            }));
            dispatch(saveBrochureRequest({
              ...brochure,
              editorJSON: stateBrochure.editorJSON,
              brochureId: brochure.id,
              savedBrochureId: brochure.id,
              doSaveToDatabase: true,
              doShowBrochureEditor: true,
            }));
            track('brochureEditor.onSaveContent');
          }}
          disabled={saveBrochureStatus === SearchStatus.SEARCHING}
          disableElevation
          variant="contained"
          color="primary"
        >
          Save
          {' '}
          {displayLabel}
        </Button>
      </div>
      )}
    </div>
  );
};
TipTapEditor.propTypes = {
  brochure: PropTypes.shape({
    editorJSON: PropTypes.string,
    trips: PropTypes.objectOf(PropTypes.shape({})),
    id: PropTypes.string,
  }),
  isVisible: PropTypes.bool,
  isEditable: PropTypes.bool,
  isBrochurePage: PropTypes.bool,
  richTextButtonStyles: PropTypes.func,
  showSpinner: PropTypes.func,
};
TipTapEditor.defaultProps = {
  brochure: undefined,
  isVisible: true,
  isEditable: true,
  isBrochurePage: false,
  richTextButtonStyles: undefined,
  showSpinner: undefined,
};
export default TipTapEditor;
